/**
 * WCAG kontrast pre white-label vrstvu.
 *
 * Partner mení jedinú farbu — `--color-brand`. Text na nej (`--color-on-brand`)
 * sa preto NIKDY nezadáva ručne: pri žltej by partner nechal bielu a primárne
 * tlačidlo by spadlo pod 2:1. Odvodí sa výpočtom podľa pravidla z `tokens.css`:
 * porovnaj kontrast brandu voči bielej a voči `#0F172A`, vyhráva vyšší.
 *
 * Modul je zámerne čistý (žiadny DOM, žiadny Vue) — rovnako ako cenové jadro
 * v `Domain/Pricing`. Vďaka tomu sa dá otestovať tabuľkou farieb.
 */

/** Svetlé popredie — biela zo `--color-surface`. */
export const ON_BRAND_LIGHT = '#FFFFFF'

/** Tmavé popredie — `--color-fg` zo zamknutej neutrálnej palety. */
export const ON_BRAND_DARK = '#0F172A'

/**
 * Hranica, pod ktorou sa farba považuje za tmavú.
 *
 * Používa ju upozornenie na bimetalový efekt (tmavá brána nad 4 m na slnku),
 * preto je to konštanta a nie magické číslo v komponente.
 */
export const DARK_COLOR_LUMINANCE = 0.25

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/** Chyba dát, nie používateľa: do funkcie sa dostal reťazec, ktorý nie je farba. */
export class InvalidHexColorError extends Error {
	constructor(public readonly value: string) {
		super(`Neplatný hex kód farby: ${value}`)
		this.name = 'InvalidHexColorError'
	}
}

/** Strážca pre validáciu formulára — umožní odmietnuť vstup skôr, než sa počíta. */
export function isHexColor(value: string): boolean {
	return HEX_PATTERN.test(value.trim())
}

/**
 * Znormalizuje `#abc` aj `abcdef` na `#AABBCC`.
 *
 * Partneri zadávajú farbu z brand manuálu v rôznych tvaroch; porovnávať
 * a ukladať sa má jeden.
 */
export function normalizeHex(value: string): string {
	const match = HEX_PATTERN.exec(value.trim())
	if (match === null) {
		throw new InvalidHexColorError(value)
	}

	const digits = match[1] as string
	const full =
		digits.length === 3
			? digits
					.split('')
					.map((digit) => digit + digit)
					.join('')
			: digits

	return `#${full.toUpperCase()}`
}

/** Rozloží hex na tri kanály 0–255. */
function channels(hex: string): [number, number, number] {
	const normalized = normalizeHex(hex)

	return [
		Number.parseInt(normalized.slice(1, 3), 16),
		Number.parseInt(normalized.slice(3, 5), 16),
		Number.parseInt(normalized.slice(5, 7), 16),
	]
}

/** Linearizácia sRGB kanála podľa WCAG 2.1 (gamma korekcia). */
function linearize(channel: number): number {
	const srgb = channel / 255

	return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4)
}

/**
 * Relatívna luminancia podľa WCAG 2.1.
 *
 * Koeficienty 0,2126 / 0,7152 / 0,0722 nie sú voliteľné — sú súčasťou normy.
 */
export function relativeLuminance(hex: string): number {
	const [red, green, blue] = channels(hex)

	return 0.2126 * linearize(red) + 0.7152 * linearize(green) + 0.0722 * linearize(blue)
}

/**
 * Alias pod menom z mockupov (`lum()` v `konfigurator.dc.html`).
 *
 * Handoff hovorí „preniesť 1:1“ — toto je tá istá funkcia pod tým istým menom,
 * aby sa dvojica `lum()` / `onBrandFor()` dala v kóde nájsť podľa dokumentácie.
 */
export const lum = relativeLuminance

/** Kontrastný pomer dvoch farieb, vždy ≥ 1 (poradie argumentov nerozhoduje). */
export function contrastRatio(foreground: string, background: string): number {
	const first = relativeLuminance(foreground)
	const second = relativeLuminance(background)
	const lighter = Math.max(first, second)
	const darker = Math.min(first, second)

	return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Vráti farbu textu na brand ploche.
 *
 * Pravidlo z `tokens.css`: kontrast(brand, biela) ≥ kontrast(brand, #0F172A)
 * → biela, inak tmavá. Pri zhode vyhráva biela, lebo tak je to v mockupoch
 * (`white-label-dokaz.dc.html`) aj v predvolenom tokene.
 *
 * Overené prípady: #1E40AF → biela, #FACC15 → #0F172A, #DC2626 → biela.
 *
 * Odchýlka od prototypu: `onBrandFor()` v `konfigurator.dc.html` počíta druhý
 * kontrast voči čiernej (`(L+0,05)/0,05`), nie voči `#0F172A`. Tu platí znenie
 * z `tokens.css` a `MASTER.md`, ktoré menujú `#0F172A`. Všetky tri
 * zdokumentované farby vychádzajú rovnako; líšia sa len farby s luminanciou
 * medzi 0,179 a 0,199 (stredné sivé), kde je správna práve tmavá voľba —
 * text sa porovnáva s farbou, ktorá sa naozaj vykreslí.
 */
export function onBrandFor(brandHex: string): string {
	const normalized = normalizeHex(brandHex)
	const againstLight = contrastRatio(normalized, ON_BRAND_LIGHT)
	const againstDark = contrastRatio(normalized, ON_BRAND_DARK)

	return againstLight >= againstDark ? ON_BRAND_LIGHT : ON_BRAND_DARK
}

/**
 * Kontrast dvojice brand + odvodené popredie.
 *
 * Používa ho admin obrazovka partnera: pri hraničných farbách musí obchodník
 * vidieť číslo, nie len výsledok — inak neverí, že je voľba správna.
 */
export function onBrandContrast(brandHex: string): number {
	return contrastRatio(brandHex, onBrandFor(brandHex))
}

/** Tmavá farba brány (bimetalový efekt) aj tmavý brand (voľba ikon v PDF). */
export function isDarkColor(hex: string, threshold: number = DARK_COLOR_LUMINANCE): boolean {
	return relativeLuminance(hex) < threshold
}
